export type ContactSelectionExclusionSnapshot = {
  revision: number;
  excludedContactIds: string[];
};

export type ContactSelectionExclusionWriteResult =
  | ({ ok: true } & ContactSelectionExclusionSnapshot)
  | ({ ok: false; conflict: true } & ContactSelectionExclusionSnapshot);

type QueueCallbacks = {
  onAcknowledged: (snapshot: ContactSelectionExclusionSnapshot) => void;
  onConflict: (snapshot: ContactSelectionExclusionSnapshot) => void;
  onError: (error: unknown, acknowledged: ContactSelectionExclusionSnapshot) => void;
  onPendingChange: (pending: boolean) => void;
};

// Exclusion writes still saving, by conversation. The next chat turn in that
// conversation waits for them (settlePendingExclusionWrites), so a segment the
// assistant drafts right after "the list is ready" never freezes a list
// missing the latest uncheck: the draft reads the exclusions the server has
// acknowledged. A stopped queue leaves at once, so a closed card or another
// conversation never delays a send.
const savingDrains = new Map<Promise<void>, string>();
export const EXCLUSION_SETTLE_TIMEOUT_MS = 15_000;

/** Resolves once the conversation's exclusion writes in flight have settled, or after the timeout. Never rejects. */
export function settlePendingExclusionWrites(threadId: string, timeoutMs = EXCLUSION_SETTLE_TIMEOUT_MS): Promise<void> {
  const pending = [...savingDrains].filter(([, owner]) => owner === threadId).map(([drain]) => drain);
  if (pending.length === 0) return Promise.resolve();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  return Promise.race([Promise.allSettled(pending).then(() => undefined), timeout])
    .finally(() => clearTimeout(timer));
}

// One instance is owned by one actor/org/thread/token card identity. It keeps
// at most one request in flight and coalesces later checkbox states behind it;
// every request carries the last acknowledged server revision.
export class ContactSelectionExclusionQueue {
  private acknowledged: ContactSelectionExclusionSnapshot;
  private desired: string[] | null = null;
  private draining: Promise<void> | null = null;
  private stopped = false;

  constructor(
    initial: ContactSelectionExclusionSnapshot,
    private readonly write: (
      expectedRevision: number,
      excludedContactIds: string[],
    ) => Promise<ContactSelectionExclusionWriteResult>,
    private readonly callbacks: QueueCallbacks,
    // The conversation whose next turn waits for this queue's writes.
    private readonly threadId = '',
  ) {
    this.acknowledged = normalizeSnapshot(initial);
  }

  replace(excludedContactIds: string[]) {
    if (this.stopped) return;
    this.desired = normalizeIds(excludedContactIds);
    this.callbacks.onPendingChange(true);
    if (!this.draining) {
      const draining = this.drain().finally(() => {
        this.draining = null;
        savingDrains.delete(draining);
      });
      this.draining = draining;
      savingDrains.set(draining, this.threadId);
    }
  }

  stop() {
    this.stopped = true;
    this.desired = null;
    if (this.draining) savingDrains.delete(this.draining);
  }

  snapshot() {
    return normalizeSnapshot(this.acknowledged);
  }

  private async drain() {
    while (!this.stopped && this.desired) {
      const candidate = this.desired;
      this.desired = null;
      try {
        const result = await this.write(this.acknowledged.revision, candidate);
        if (this.stopped) return;
        const snapshot = normalizeSnapshot(result);
        if (!result.ok) {
          this.acknowledged = snapshot;
          this.desired = null;
          this.callbacks.onConflict(snapshot);
          this.callbacks.onPendingChange(false);
          return;
        }
        if (
          snapshot.revision !== this.acknowledged.revision + 1 ||
          !sameIds(snapshot.excludedContactIds, candidate)
        ) {
          throw new Error('The exclusion update acknowledgement did not match the requested revision.');
        }
        this.acknowledged = snapshot;
        // Keep the card's newer optimistic intent visible while a coalesced
        // replacement is waiting. The final acknowledgement publishes it.
        if (!this.desired) this.callbacks.onAcknowledged(snapshot);
      } catch (error) {
        if (this.stopped) return;
        this.desired = null;
        this.callbacks.onError(error, this.snapshot());
        this.callbacks.onPendingChange(false);
        return;
      }
    }
    if (!this.stopped) this.callbacks.onPendingChange(false);
  }
}

function normalizeSnapshot(snapshot: ContactSelectionExclusionSnapshot) {
  return { revision: snapshot.revision, excludedContactIds: normalizeIds(snapshot.excludedContactIds) };
}

function normalizeIds(ids: string[]) {
  return [...new Set(ids)].sort((left, right) => left.localeCompare(right));
}

function sameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}
